import React from 'react';
import SorotanCard from './SorotanCard';
import KartuPemainCard from './KartuPemainCard';
import RaporEventStoryCard from './RaporEventStoryCard';
import { RaporEventRacketCard } from './RaporEventRacketCard';
import { KlasemenCard, KlasemenStandingItem } from './KlasemenCard';
import { LeaderboardRecapCard } from './LeaderboardRecapCard';

export type RecapTemplateVariant =
  | 'sorotan'
  | 'kartu_pemain'
  | 'rapor_event'
  | 'rapor_event_racket'
  | 'klasemen'
  | 'leaderboard'
  | 'leaderboard_top_score'
  | 'leaderboard_top_assist'
  | 'leaderboard_top_mmr'
  | 'leaderboard_top_save'
  | 'leaderboard_padel'
  | 'leaderboard_klasemen'
  | 'padel_klasemen'
  | 'top_score'
  | 'top_assist'
  | 'top_mmr'
  | 'top_save'
  | 'highlight'
  | 'player_card'
  | 'event_report'
  | 'full'
  | 'v4'
  | 'v5'
  | 'transparent';

export interface RecapCardRendererProps {
  template?: RecapTemplateVariant | string;
  eventName: string;
  eventDateFormatted: string;
  playerName: string;
  playerAvatarUrl?: string;
  rank?: number;
  stats: {
    goals: number;
    assists: number;
    matches: number;
    win_rate?: number;
    wins?: number;
    point?: number;
    point_loss?: number;
    is_racket?: boolean;
    rank?: number;
    cleansheet?: number;
    key_passes?: number;
    saves?: number;
    clearances?: number;
    fouls?: number;
    motm?: number;
    freestyle?: number;
    winstreak?: number;
    selectedStatKeys?: string[];
    [key: string]: any;
  };
  standings?: KlasemenStandingItem[];
  totalPlayers?: number;
  location?: string;
  sportCategory?: string;
  deltaMMR?: number | null;
  bgImage?: string;
  transparent?: boolean;
  className?: string;
}

export const RecapCardRenderer: React.FC<RecapCardRendererProps> = ({
  template = 'sorotan',
  eventName,
  eventDateFormatted,
  playerName,
  playerAvatarUrl,
  rank,
  stats,
  standings,
  totalPlayers,
  location,
  sportCategory,
  deltaMMR,
  bgImage,
  transparent = true,
  className = '',
}) => {
  const goals = stats.goals || 0;
  const assists = stats.assists || 0;
  const matches = stats.matches || 0;

  let winRate = stats.win_rate;
  let wins = stats.wins;

  if (wins === undefined) {
    wins = winRate !== undefined ? Math.round((winRate * matches) / 100) : 0;
  }
  if (winRate === undefined) {
    winRate = matches > 0 ? Math.round((wins / matches) * 100) : 0;
  }

  const point = stats.point;
  const pointLoss = stats.point_loss;
  const isRacket = stats.is_racket || (sportCategory ? ['padel', 'badminton'].some(s => sportCategory.toLowerCase().includes(s)) : false);
  const effectiveRank = rank || stats.rank || 1;

  const normalizedVariant = (template || 'sorotan').toLowerCase();

  switch (normalizedVariant) {
    case 'rapor_event_racket':
    case 'v4':
      return (
        <RaporEventRacketCard
          playerName={playerName}
          eventName={eventName}
          eventDateFormatted={eventDateFormatted}
          location={location}
          sportCategory={sportCategory}
          rank={effectiveRank}
          matches={matches}
          winRate={winRate}
          point={point || 0}
          pointLoss={pointLoss || 0}
          bgImage={bgImage}
          transparent={transparent}
          className={className}
        />
      );

    case 'klasemen':
    case 'v5':
      return (
        <KlasemenCard
          eventName={eventName}
          eventDateFormatted={eventDateFormatted}
          location={location}
          totalPlayers={totalPlayers}
          playerName={playerName}
          standings={standings}
          bgImage={bgImage}
          transparent={transparent}
          className={className}
        />
      );

    case 'leaderboard':
    case 'leaderboard_top_score':
    case 'leaderboard_top_assist':
    case 'leaderboard_top_mmr':
    case 'leaderboard_top_save':
    case 'top_score':
    case 'top_assist':
    case 'top_mmr':
    case 'top_save':
      return (
        <LeaderboardRecapCard
          type={normalizedVariant.replace('leaderboard_', '').replace('_', '-')}
          eventName={eventName}
          eventDateFormatted={eventDateFormatted}
          location={location}
          sportCategory={sportCategory}
          players={standings as any}
          bgImage={bgImage}
          transparent={transparent}
          className={className}
        />
      );

    case 'leaderboard_padel':
    case 'leaderboard_klasemen':
    case 'padel_klasemen':
      return (
        <LeaderboardRecapCard
          type="padel-klasemen"
          eventName={eventName}
          eventDateFormatted={eventDateFormatted}
          location={location}
          sportCategory={sportCategory || 'Padel'}
          players={standings as any}
          bgImage={bgImage}
          transparent={transparent}
          className={className}
        />
      );

    case 'kartu_pemain':
    case 'player_card':
    case 'v2':
      return (
        <KartuPemainCard
          playerName={playerName}
          playerAvatarUrl={playerAvatarUrl}
          eventName={eventName}
          eventDateFormatted={eventDateFormatted}
          location={location}
          sportCategory={sportCategory}
          deltaMMR={deltaMMR}
          matches={matches}
          goals={goals}
          assists={assists}
          winRate={winRate}
          point={point}
          pointLoss={pointLoss}
          isRacket={isRacket}
          selectedStatKeys={stats.selectedStatKeys}
          rawStats={stats}
          bgImage={bgImage}
          transparent={transparent}
          className={className}
        />
      );

    case 'rapor_event':
    case 'event_report':
    case 'full':
    case 'v1':
      return (
        <RaporEventStoryCard
          playerName={playerName}
          playerAvatarUrl={playerAvatarUrl}
          eventName={eventName}
          eventDateFormatted={eventDateFormatted}
          location={location}
          sportCategory={sportCategory}
          deltaMMR={deltaMMR}
          goals={goals}
          assists={assists}
          wins={wins}
          matches={matches}
          point={point}
          pointLoss={pointLoss}
          isRacket={isRacket}
          selectedStatKeys={stats.selectedStatKeys}
          rawStats={stats}
          bgImage={bgImage}
          transparent={transparent}
          className={className}
        />
      );

    case 'sorotan':
    case 'highlight':
    case 'v3':
    default:
      return (
        <SorotanCard
          eventName={eventName}
          matches={matches}
          goals={goals}
          assists={assists}
          winRate={winRate}
          point={point}
          pointLoss={pointLoss}
          isRacket={isRacket}
          selectedStatKeys={stats.selectedStatKeys}
          rawStats={stats}
          bgImage={bgImage}
          transparent={transparent}
          className={className}
        />
      );
  }
};

export default RecapCardRenderer;
